import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ExcelParserService } from '../../samples/excel/excel-parser.service';
import { ParsedSampleSheet } from '../../samples/excel/excel-parser.model';
import {
    ExcelFile,
    MarshalledData,
    Sample,
    SampleSet,
    SampleSubmission,
    SampleSubmissionResult
} from '../../samples/model/sample-management.model';
import { SamplesMainData } from '../../samples/state/samples.reducer';
import { InstitutionDTO } from '../../user/model/institution.model';
import { Credentials, RegistrationDetails, TokenizedUser } from '../../user/model/user.model';
import { ClientError, EndpointError, InvalidEmailError } from '../model/client-error';
import { ExcelVersionError, InputChangedError, InvalidInputError } from '../model/data-service-error';
import {
    NewPasswordRequestDTO,
    PostSubmittedRequestDTO,
    PutSamplesJSONRequestDTO,
    PutValidatedRequestDTO,
    RegistrationDetailsDTO,
    ResetRequestDTO,
    UserConsentRequestDTO,
    UserEmailNotificationRequestDTO
} from '../model/request.model';
import {
    ActivationResponseDTO,
    ClientDashboardInfo,
    ClientDashboardInfoDTO,
    FaqResponseDTO,
    InstituteCollectionDTO,
    NRLCollectionDTO,
    NRLDTO,
    PasswordResetRequestResponseDTO,
    PostSubmittedResponseDTO,
    PutSamplesJSONResponseDTO,
    PutSamplesXLSXResponseDTO,
    PutValidatedResponseDTO,
    RegistrationRequestResponseDTO,
    SystemInformationResponseDTO,
    TokenRefreshResponseDTO,
    TokenizedUserDTO,
    UserConsentResponseDTO,
    UserEmailNotificationResponseDTO,
    ZomoPlanFileCollectionDTO,
    ZomoPlanFileInfo,
    ZomoPlanFileData,
    OrderEntryDTO,
    OrderCollectionDTO,
    OrderDeletionResultDTO,
    SamplesWithResultsCollectionDTO
} from '../model/response.model';
import {
    AnnotatedOrderDTO
} from '../model/shared-dto.model';
import { environment } from './../../../environments/environment';
import { DTOFactoryService } from './dto-factory.service';
import { EntityFactoryService } from './entity-factory.service';

@Injectable({
    providedIn: 'root'
})
export class DataService {

    private API_VERSION = 'v2';
    private USER = 'users';
    private SAMPLE = 'samples';
    private TOKEN = 'tokens';
    private INSTITUTE = 'institutes';
    private NRL = 'nrls';
    private CLIENT_DASHBOARD_INFO = 'client-dashboard-info';
    private ZOMO_PLAN_FILE = 'zomo-plan-file';
    private ORDER = 'orders';
    private URL = {
        submit: [this.API_VERSION, this.SAMPLE, 'submitted'].join('/'),
        validate: [this.API_VERSION, this.SAMPLE, 'validated'].join('/'),
        marshal: [this.API_VERSION, this.SAMPLE].join('/'),
        unmarshal: [this.API_VERSION, this.SAMPLE].join('/'),
        institutions: [this.API_VERSION, this.INSTITUTE].join('/'),
        clientDashboardInfo: [this.API_VERSION, this.CLIENT_DASHBOARD_INFO].join('/'),
        zomoPlanFileInfos: [this.API_VERSION, this.ZOMO_PLAN_FILE].join('/'),
        downloadZomoPlanFile: [this.API_VERSION, this.ZOMO_PLAN_FILE, 'download'].join('/'),
        nrls: [this.API_VERSION, this.NRL].join('/'),
        orderList: [this.API_VERSION, this.ORDER].join('/'),
        login: [this.API_VERSION, this.USER, 'login'].join('/'),
        consent: [this.API_VERSION, this.USER, 'consent'].join('/'),
        emailNotifications: [this.API_VERSION, this.USER, 'email-notifications'].join('/'),
        register: [this.API_VERSION, this.USER, 'registration'].join('/'),
        resetPasswordRequest: [this.API_VERSION, this.USER, 'reset-password-request'].join('/'),
        resetPassword: [this.API_VERSION, this.USER, 'reset-password'].join('/'),
        verifyEmail: [this.API_VERSION, this.USER, 'verification'].join('/'),
        activateAccount: [this.API_VERSION, this.USER, 'activation'].join('/'),
        refresh: [this.API_VERSION, this.TOKEN].join('/'),
        systemInfo: [this.API_VERSION, 'info'].join('/'),
        faq: './assets/faq.json'
    };

    private LOCAL_STORAGE_CURRENT_USER = 'currentUser';

    private PARSE_OPTIONS = {
        headers: new HttpHeaders({
            'X-Parse-Application-Id': environment.appId
        })
    };


    constructor(
        private httpClient: HttpClient,
        private dtoService: DTOFactoryService,
        private entityFactoryService: EntityFactoryService,
        private excelParser: ExcelParserService) {
    }

    getCurrentUser(): TokenizedUser | null {
        const userJson: string | null = localStorage.getItem(this.LOCAL_STORAGE_CURRENT_USER);
        if (userJson !== null) {
            return JSON.parse(userJson);
        }
        return null;
    }

    setCurrentUser(user: TokenizedUser): void {
        localStorage.setItem(this.LOCAL_STORAGE_CURRENT_USER, JSON.stringify(user));
    }

    removeCurrentUser(): void {
        localStorage.removeItem(this.LOCAL_STORAGE_CURRENT_USER);
    }

    getFaq(): Observable<FaqResponseDTO> {
        return this.httpClient.get<FaqResponseDTO>(this.URL.faq, this.PARSE_OPTIONS);
    }

    getSystemInfo(): Observable<SystemInformationResponseDTO> {
        return this.httpClient.get<SystemInformationResponseDTO>(this.URL.systemInfo, this.PARSE_OPTIONS);
    }

    login(credentials: Credentials): Observable<TokenizedUser> {
        return this.httpClient.post<TokenizedUserDTO>(this.URL.login, credentials, this.PARSE_OPTIONS).pipe(
            map((dto: TokenizedUserDTO) => dto)
        );
    }

    saveDataSaveConsent(dataSaveAgreed: boolean): Observable<UserConsentResponseDTO> {
        const requestDTO: UserConsentRequestDTO = { dataSaveAgreed: dataSaveAgreed };
        return this.httpClient.patch<UserConsentResponseDTO>(this.URL.consent, requestDTO, this.PARSE_OPTIONS);
    }

    saveEmailNotificationSettings(
        settings: UserEmailNotificationRequestDTO
    ): Observable<UserEmailNotificationResponseDTO> {
        return this.httpClient.patch<UserEmailNotificationResponseDTO>(
            this.URL.emailNotifications,
            settings,
            this.PARSE_OPTIONS
        );
    }

    sendSampleSheet(sendableFormData: SampleSubmission): Observable<SampleSubmissionResult> {
        const requestDTO: PostSubmittedRequestDTO = {
            order: { sampleSet: this.dtoService.fromSampleSet(sendableFormData.order) },
            comment: sendableFormData.comment,
            receiveAs: sendableFormData.receiveAs.toString()
        };
        return this.httpClient.post<PostSubmittedResponseDTO>(this.URL.submit, requestDTO, this.PARSE_OPTIONS).pipe(
            map((dto: PostSubmittedResponseDTO) => ({
                samples: dto.order.sampleSet.samples.map(sample => this.entityFactoryService.toSample(sample)),
                // A server that does not report on the copy is assumed to have
                // sent it, which is how it behaved before the flag existed.
                customerCopySent: dto.customerCopySent !== false
            })),
            catchError((err) => {
                if (err instanceof EndpointError) {
                    if (err.errorDTO.order) {
                        const order: AnnotatedOrderDTO = err.errorDTO.order;
                        const samples: Sample[] = order.sampleSet.samples.map(
                            sample => this.entityFactoryService.toSample(sample)
                        );
                        switch (err.errorDTO.code) {
                            case 5:
                                throw new InvalidInputError(samples, 'Invalid sample data error.');
                            case 6:
                                throw new InputChangedError(samples, 'Input changed error.');
                            default:
                                throw err;
                        }
                    }
                }
                throw err;
            })
        );
    }

    validateSampleData(requestData: SamplesMainData): Observable<Sample[]> {
        const requestDTO: PutValidatedRequestDTO = { order: this.dtoService.fromSamplesMainDataToOrderDTO(requestData) };
        return this.httpClient.put<PutValidatedResponseDTO>(this.URL.validate, requestDTO, this.PARSE_OPTIONS).pipe(
            map((dto: PutValidatedResponseDTO) =>
                dto.order.sampleSet.samples.map(sample =>
                    this.entityFactoryService.toSample(sample)
                )));
    }

    unmarshalExcel(requestData: ExcelFile): Observable<SampleSet> {
        // MPS-312: the .xlsx is parsed to JSON in the browser and only JSON is sent
        // to the API. Server-side NRL enrichment then turns it into a full SampleSet.
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Parse-Application-Id': environment.appId
            })
        };
        return from(this.excelParser.parse(requestData.file)).pipe(
            switchMap((parsedSampleSheet: ParsedSampleSheet) =>
                this.httpClient.put<PutSamplesJSONResponseDTO>(
                    this.URL.unmarshal,
                    { parsedSampleSheet: parsedSampleSheet },
                    httpOptions
                )
            ),
            map((dto: PutSamplesJSONResponseDTO) => this.entityFactoryService.toSampleSet(dto.order.sampleSet)),
            catchError((error) => {
                if (error instanceof EndpointError) {
                    if (error.errorDTO.version) {
                        const excelVersion: string = error.errorDTO.version;
                        const currentVersions: string[] = error.errorDTO.currentVersions || [];
                        switch (error.errorDTO.code) {
                            case 7:
                                throw new ExcelVersionError(excelVersion, currentVersions, 'Invalid excel version error.');
                            default:
                                throw error;
                        }
                    }

                    if (error.errorDTO.code) {
                        switch (error.errorDTO.code) {
                            case 8:
                                throw new InvalidEmailError('Invalid email address');
                            default:
                                throw error;
                        }
                    }
                }

                throw new ClientError(`Failed to read excel file. error=${error}`);
            }));
    }

    marshalJSON(requestData: SamplesMainData): Observable<MarshalledData> {
        const requestBody: PutSamplesJSONRequestDTO = { order: this.dtoService.fromSamplesMainDataToOrderDTO(requestData) };
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'multipart/form-data',
                'X-Parse-Application-Id': environment.appId
            })
        };
        return this.httpClient.put<PutSamplesXLSXResponseDTO>(this.URL.marshal, requestBody, httpOptions).pipe(
            map((dto: PutSamplesXLSXResponseDTO) => this.entityFactoryService.fromPutSamplesXLSXResponseDTOToMarshalledData(dto)));
    }

    downloadZomoPlanFile(zomoPlanFileInfo: ZomoPlanFileInfo): Observable<ZomoPlanFileData> {
        const requestBody: ZomoPlanFileInfo = zomoPlanFileInfo;
        const httpOptions = {
            headers: new HttpHeaders({
                'X-Parse-Application-Id': environment.appId
            }),
            responseType: 'blob' as 'json',
            observe: 'response' as 'body'
        };

        return this.httpClient.put<HttpResponse<Blob>>(this.URL.downloadZomoPlanFile, requestBody, httpOptions).pipe(
            map((response: HttpResponse<Blob>) => this.entityFactoryService.fromBlobResponseToZomoPlanFileData(
                response,
                zomoPlanFileInfo.year
            )));
    }

    getAllInstitutions(): Observable<InstitutionDTO[]> {
        return this.httpClient.get<InstituteCollectionDTO>(this.URL.institutions, this.PARSE_OPTIONS).pipe(
            map((dto: InstituteCollectionDTO) => dto.institutes));
    }

    getClientDashboardInfo(): Observable<ClientDashboardInfo> {
        return this.httpClient.get<ClientDashboardInfoDTO>(this.URL.clientDashboardInfo, this.PARSE_OPTIONS).pipe(
            map((dto: ClientDashboardInfoDTO) => dto.clientDashboardInfo)
        );
    }

    getAllZomoPlanFiles(): Observable<ZomoPlanFileInfo[]> {
        return this.httpClient.get<ZomoPlanFileCollectionDTO>(this.URL.zomoPlanFileInfos, this.PARSE_OPTIONS).pipe(
            map((dto: ZomoPlanFileCollectionDTO) => dto.zomoPlanFiles));
    }

    getAllNRLs(): Observable<NRLDTO[]> {
        return this.httpClient.get<NRLCollectionDTO>(this.URL.nrls, this.PARSE_OPTIONS).pipe(
            map((dto: NRLCollectionDTO) => dto.nrls));
    }

    getOrderList(): Observable<OrderEntryDTO[]> {
        return this.httpClient.get<OrderCollectionDTO>(this.URL.orderList, this.PARSE_OPTIONS).pipe(
            map((dto: OrderCollectionDTO) => dto.orders));
    }

    deleteAllOrders(): Observable<OrderDeletionResultDTO> {
        return this.httpClient.delete<OrderDeletionResultDTO>(this.URL.orderList, this.PARSE_OPTIONS);
    }

    getSamplesWithResults(orderId: string): Observable<SamplesWithResultsCollectionDTO> {
        const url = [this.URL.orderList, 'samples-with-results'].join('/');
        return this.httpClient.post<SamplesWithResultsCollectionDTO>(url, { orderId: orderId }, this.PARSE_OPTIONS);
    }

    registrationRequest(registrationDetails: RegistrationDetails): Observable<RegistrationRequestResponseDTO> {
        const registrationDetailsDTO: RegistrationDetailsDTO = { ...registrationDetails };
        return this.httpClient.post<RegistrationRequestResponseDTO>(this.URL.register, registrationDetailsDTO);
    }

    resetPasswordRequest(email: string): Observable<PasswordResetRequestResponseDTO> {
        const resetRequest: ResetRequestDTO = { email: email };
        return this.httpClient.put<PasswordResetRequestResponseDTO>(this.URL.resetPasswordRequest, resetRequest, this.PARSE_OPTIONS);
    }

    resetPassword(password: string, token: string) {
        const newPassword: NewPasswordRequestDTO = { password: password };
        return this.httpClient.patch([this.URL.resetPassword, token].join('/'), newPassword, this.PARSE_OPTIONS);
    }

    verifyEmail(token: string): Observable<boolean> {
        return this.httpClient.patch<ActivationResponseDTO>([this.URL.verifyEmail, token].join('/'), null, this.PARSE_OPTIONS).pipe(
            map(r => r.activation)
        );
    }

    activateAccount(adminToken: string): Observable<ActivationResponseDTO> {
        return this.httpClient.patch<ActivationResponseDTO>([this.URL.activateAccount, adminToken].join('/'), null, this.PARSE_OPTIONS);
    }

    refreshToken(): Observable<TokenRefreshResponseDTO> {
        return this.httpClient.post<TokenRefreshResponseDTO>(this.URL.refresh, null, this.PARSE_OPTIONS);
    }
}
